import type { AppConfig } from '../api'
import type { ClientPlatform } from '@/lib/config/env'
import type { UpdateDecision } from '../state/gate-store'

/** Numeric, three-part comparison; missing parts are zero (1.2 == 1.2.0). */
export function compareVersions(current: string, minimum: string): number {
  const parse = (value: string): number[] => {
    const parts = value
      .trim()
      .split('.')
      .slice(0, 3)
      .map((part) => {
        const parsed = Number.parseInt(part, 10)
        return Number.isFinite(parsed) ? parsed : 0
      })
    return [parts[0] ?? 0, parts[1] ?? 0, parts[2] ?? 0]
  }

  const left = parse(current)
  const right = parse(minimum)
  for (let index = 0; index < 3; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index]
  }
  return 0
}

/**
 * D-043: use the platform-specific store URL and respect `forceUpdate`.
 *
 * The gate is shown when the client is older than `minimumAppVersion`, or
 * whenever the operator forces the update. Web has no store URL and shows
 * refresh guidance instead, so it is never a dead end.
 */
export function evaluateUpdate(
  config: AppConfig,
  {
    version,
    platform,
  }: {
    version: string
    platform: ClientPlatform
  },
): UpdateDecision {
  const forced = config.forceUpdate === true
  const outdated = compareVersions(version, config.minimumAppVersion) < 0
  return {
    required: forced || outdated,
    forced,
    currentVersion: version,
    minimumVersion: config.minimumAppVersion,
    storeUrl: selectStoreUrl(config, platform),
    platform,
  }
}

export function selectStoreUrl(
  config: AppConfig,
  platform: ClientPlatform,
): string | null {
  if (platform === 'ios') return config.iosStoreUrl ?? null
  if (platform === 'android') return config.androidStoreUrl ?? null
  return null
}
